import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  styled,
  tableCellClasses,
  FormControlLabel
} from '@mui/material'
import Pagination from '../../../../components/TablePagination'
import React, { useEffect, useRef } from 'react'
import { Button as GenericButton } from '../../../../components/Button'
import { type ConnectedProps, connect, useSelector } from 'react-redux'
import { type RootState } from '../../../../store'
import { visuallyHidden } from '@mui/utils'
import AuthError from '../../../../components/ErrorComponents/AuthError'
import { type HsmRegularKEK } from '../../types'
import { type RegularKekListState } from '../../redux/RegularKekListSlice'
import { type getAllRegularKekParameterState } from '../../redux/getAllRegularKekFilterParameterSlice'
import LanguageStrings from '../../../../i18n/locales'
import {
  downloadregularKekByLabel, fetchRegularKEKList,
  exportSMimeCertificate,
  getAllRegularKekFilterParameterInfo,
  getAllRegularKekParameterInfo,
  fetchRegularKekHsmListWithStatus,
  fetchRegularKekHsmListWithStatusAndFilters
} from '../../redux/actionCreator'
import SearchBox from '../../../../components/SearchBox'
import CloseIcon from '@mui/icons-material/Close'
import ReplayIcon from '@mui/icons-material/Replay'
import FilterIcon from '../../../../assets/filterIcon.png'
import { GenXDropdown, type Option } from '../../../../components/GenXDropdown'
import { type RegularKekHsmStatusState } from '../../redux/regularKekHsmStatusSlice'

const HSMConfigurationRegularKekStrings = LanguageStrings().HSMConfiguration.RegularKek

type Order = 'asc' | 'desc'

interface Sort {
  order: Order
  orderBy: string
}

const style = {
  width: '99%',
  bgcolor: '#313030',
  border: '1px solid #101010',
  borderRadius: '10px',
  boxShadow: '1px 1px 1px rgba(0, 0, 0, 0.15)',
  marginBottom: '16px',
  padding: 0
}

interface EnhancedTableProps {
  numSelected: number
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void
  rowCount: number
  dataList: HsmRegularKEK[]
  order: Order
  orderBy: string
  onRequestSort: (event: React.MouseEvent<unknown>, newOrderBy: keyof HsmRegularKEK) => void
}

const Uses: Record<number, string> = {
  1: 'Import',
  2: 'Wrap',
  3: 'S/MIME'
}

function getUseString (statusCode: number): string {
  return Uses[statusCode] !== undefined && Uses[statusCode] !== '' ? Uses[statusCode] : 'Unknown Status'
}

function EnhancedTableHead (props: EnhancedTableProps): any {
  const { onSelectAllClick, order, orderBy, onRequestSort, numSelected, rowCount, dataList } = props
  const createSortHandler =
        (newOrderBy: keyof HsmRegularKEK) => (event: React.MouseEvent<unknown>) => {
          onRequestSort(event, newOrderBy)
        }
  return (dataList !== null && dataList !== undefined && <>
        <TableHead data-testid='table-header'>
          <TableRow>
            <StyledTableCell padding="checkbox">
              <Checkbox
                sx={{ color: '#909090' }}
                data-testid="device-table-header-checkbox"
                indeterminate={numSelected > 0 && numSelected < rowCount}
                checked={rowCount > 0 && numSelected === rowCount}
                onChange={onSelectAllClick}
                inputProps={{
                  'aria-label': 'Select all Assets',
                  id: 'select-all-items'
                }}
              />
            </StyledTableCell>
              {headCells.map((headCell: HeadCell) => (
                  <StyledTableCell
                      key={headCell.id}
                      align='left'
                      padding={headCell.disablePadding ? 'none' : 'normal'}
                      sortDirection={orderBy === headCell.id ? order : false}
                  >
                      <TableSortLabel
                          hideSortIcon={!headCell.sortable}
                          direction={orderBy === headCell.id ? order : 'asc'}
                          onClick={headCell.sortable ? createSortHandler(headCell.id) : undefined}
                          aria-label={headCell.label}
                          data-testid="test-sort"
                          sx={{ cursor: headCell.sortable ? 'pointer' : 'text' }}
                      >
                          {headCell.id}
                          {orderBy === headCell.id
                            ? (
                                  // istanbul ignore next
                                  <Box component="span" sx={visuallyHidden}>
                                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                  </Box>
                              )
                            : null}
                      </TableSortLabel>
                  </StyledTableCell>
              ))}
          </TableRow>
        </TableHead>
      </>
  )
}

interface HeadCell {
  disablePadding: boolean
  id: any
  label: string
  numeric: boolean
  sortable: boolean
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: '#F0F0F0',
    fontWeight: '700',
    fontSize: 12,
    border: '2px solid #202020',
    padding: '5px 16px',
    fontFamily: 'Honeywell Sans Web',
    lineHeight: '20px'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: '#d0d0d0',
    paddingLeft: '17px',
    borderBottom: '1px solid #202020',
    borderRight: 0,
    fontFamily: 'Honeywell Sans Web',
    whiteSpace: 'nowrap'
  }
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '& th': {
    border: '1px solid #202020'
  }
}))

const headCells: readonly HeadCell[] = [
  {
    id: 'label',
    numeric: false,
    disablePadding: false,
    label: HSMConfigurationRegularKekStrings.Label,
    sortable: true
  },
  {
    id: 'tenant',
    numeric: false,
    disablePadding: false,
    label: HSMConfigurationRegularKekStrings.Tenant,
    sortable: true
  },
  {
    id: 'use',
    numeric: true,
    disablePadding: false,
    label: HSMConfigurationRegularKekStrings.Use,
    sortable: true
  },
  {
    id: 'created By',
    numeric: false,
    disablePadding: false,
    label: HSMConfigurationRegularKekStrings.CreatedBy,
    sortable: true
  },
  {
    id: 'created At',
    numeric: false,
    disablePadding: false,
    label: HSMConfigurationRegularKekStrings.CreatedAt,
    sortable: true
  },
  {
    id: 'action',
    numeric: false,
    disablePadding: false,
    label: HSMConfigurationRegularKekStrings.Action,
    sortable: false
  }
]

interface FilterState {
  use: Option | null
}

interface OwnProps {
  isChecked: boolean
  setIsChecked: React.Dispatch<React.SetStateAction<boolean>>
}

const RegularKekTableComponent = (props: PropsFromRedux & OwnProps): JSX.Element => {
  const [regularKekList, setRegularKekList] = React.useState<HsmRegularKEK[]>([])
  const [selected, setSelected] = React.useState<string[]>([])
  const [checkedKek, setCheckedRegularKekList] = React.useState<HsmRegularKEK[]>([])
  const [page, setPage] = React.useState(1)
  const [sort, setSort] = React.useState<Sort>({ order: 'desc', orderBy: 'created At' })
  const [searchValue, setSearchValue] = React.useState<string | null>('')
  const rowsPerPage = 10
  const numAssignedAssets = useSelector((state: RootState) => {
    if (props.isChecked) {
      return state.regularKekHsmStatus.kekList?.total_count
    } else {
      return state.regularKekList.regularKekList?.total_count
    }
  })

  const [openFilter, setOpenFilter] = React.useState(false)
  const [showfilter, setShowFilter] = React.useState(false)
  const [useOptions, setUseOptions] = React.useState<Option[]>([])
  const [filterState, setFilterState] = React.useState<FilterState>({ use: null })
  const [appliedFilters, setAppliedFilters] = React.useState<FilterState>({ use: null })
  const KekFilterParameter = useSelector((state: RootState) => state.getAllRegularKekParameterSlice.data?.data?.use)

  const calledOnce = useRef(false)
  React.useEffect(() => {
    if (!calledOnce.current) {
      props.getAllRegularKekParameterInfo()
      handleFetchRegularKEKList()
      calledOnce.current = true
    }
  }, [])

  const handleUseSelect = (selectedValue: Option): void => {
    setFilterState({ ...filterState, use: selectedValue })
  }

  const resetFilterState = (): void => {
    setFilterState(appliedFilters)
    setOpenFilter(false)
  }

  const resetAppliedFilters = (): void => {
    setAppliedFilters({ use: null })
  }

  const showFilterDetails = (pageNum: number): void => {
    console.log('Fetching data checkbox is unchecked and filters are applied:', props.isChecked)
    props.getAllRegularKekFilterParameterInfo(
      pageNum,
      rowsPerPage,
      sort.order,
      sort.orderBy,
      searchValue ?? '',
      {
        use: filterState.use === null ? [] : [filterState.use.value]
      })
  }

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    props.setIsChecked(event.target.checked)
  }

  const filtersUpdated = (): boolean => {
    return (filterState.use !== appliedFilters.use)
  }

  const handleRowClick = (id: string): void => {
    const isIdSelected = selected.includes(id)
    if (!isIdSelected) {
      setCheckedRegularKekList([...checkedKek, regularKekList.filter((row: HsmRegularKEK) => row.id === id)[0]])
      setSelected([...selected, id])
    } else {
      const temp = [...checkedKek].filter((row: HsmRegularKEK) => row.id !== id)
      setCheckedRegularKekList(temp)
      setSelected(selected.filter((selectedId) => selectedId !== id))
    }
  }

  const handleFetchRegularKEKList = (): void => {
    console.log('Fetching data checkbox is unchecked:', props.isChecked, 'empty', props.regularKekListState.regularKekList)
    if (page >= 1) {
      if (!props.isChecked) {
        props.fetchRegularKEKList(
          page - 1, rowsPerPage, sort.order, sort.orderBy, searchValue ?? ''
        )
      }
    }
  }

  const handleFetchRegularKekHsmListWithStatus = (): void => {
    console.log('Fetching data checkbox is checked', props.isChecked)
    if (page >= 1) {
      if (props.isChecked) {
        props.fetchRegularKekHsmListWithStatus(
          page - 1, rowsPerPage, sort.order, sort.orderBy, searchValue ?? ''
        )
      }
    }
  }

  const handleFetchRegularKekHsmListWithStatusAndFilters = (): void => {
    console.log('Fetching data checkbox is checked and filters are applied:', props.isChecked)
    if (page >= 1) {
      if (props.isChecked) {
        props.fetchRegularKekHsmListWithStatusAndFilters(
          page - 1, rowsPerPage, sort.order, sort.orderBy, searchValue ?? '',
          { use: (appliedFilters.use != null) ? [appliedFilters.use.value] : [] }
        )
      }
    }
  }

  React.useEffect(() => {
    if (appliedFilters.use === null) {
      if (props.regularKekListState.regularKekList !== null && props.regularKekListState.httpStatus === 200 && (props.regularKekListState.regularKekList.total_count / rowsPerPage) >= page - 1) {
        if (props.isChecked) {
          handleFetchRegularKekHsmListWithStatus()
        } else {
          handleFetchRegularKEKList()
        }
      } else { setPage(1) }
    } else {
      if (props.hsmStatusState.kekList !== null && props.hsmStatusState.httpStatus === 200 && (props.hsmStatusState.kekList.total_count / rowsPerPage) >= page - 1) {
        if (props.isChecked) {
          handleFetchRegularKekHsmListWithStatusAndFilters()
        } else {
          setPage(1)
        }
      }
      if (props.regularKekListState.regularKekList !== null && props.regularKekListState.httpStatus === 200 && (props.regularKekListState.regularKekList.total_count / rowsPerPage) >= page - 1) {
        if (!props.isChecked) {
          showFilterDetails(page - 1)
        }
      } else {
        setPage(1)
      }
    }
  }, [props.isChecked, appliedFilters, page, sort])

  const isHsmWithFilters = props.isChecked && appliedFilters.use !== null
  const handleSearch = (): void => {
    if (page >= 1) {
      setPage(1)
      setFilterState({ use: null })
      setAppliedFilters({ use: null })
    }
  }

  React.useEffect(() => {
    if (KekFilterParameter !== null && Array.isArray(KekFilterParameter) && KekFilterParameter?.length > 0) {
      const useStatusOptions: Option[] = []
      KekFilterParameter?.forEach((item: any) => {
        useStatusOptions.push({
          value: item,
          label: getUseString(item)
        })
      })
      setUseOptions(useStatusOptions)
    }
  }, [KekFilterParameter])

  const handleRequestSort =
        (event: React.MouseEvent<unknown>, newOrderBy: keyof HsmRegularKEK): void => {
          const isAsc = sort.orderBy === newOrderBy && sort.order === 'asc'
          const toggledOrder = isAsc ? 'desc' : 'asc'
          setSort({ order: toggledOrder, orderBy: newOrderBy })
        }

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const currentPageKekList = regularKekList.map((regularKEK: HsmRegularKEK) => regularKEK.id)
    if (event.target.checked) {
      const newSelected: string[] = []
      const newSelectedKeks: HsmRegularKEK[] = []
      currentPageKekList.forEach((id) => {
        if (!selected.includes(id)) {
          newSelected.push(id)
        }
      })
      regularKekList.forEach((row: HsmRegularKEK) => {
        if (newSelected.includes(row.id)) {
          newSelectedKeks.push(row)
        }
      })
      setSelected([...selected, ...newSelected])
      setCheckedRegularKekList([...checkedKek, ...newSelectedKeks])
    } else {
      const newSelected: string[] = []
      selected.forEach((id) => {
        if (!currentPageKekList.includes(id)) {
          newSelected.push(id)
        }
      })
      const newSelectedKeks: HsmRegularKEK[] = [...checkedKek].filter((row: HsmRegularKEK) => !currentPageKekList.includes(row.id))
      setSelected(newSelected)
      setCheckedRegularKekList(newSelectedKeks)
    }
  }

  const isSelected = (id: string): boolean => selected.includes(id)

  const getDisplayKekList = (): { list: HsmRegularKEK[], total_count: number } => {
    if (props.isChecked) {
      return {
        list: props.hsmStatusState.kekList?.list ?? [],
        total_count: props.hsmStatusState.kekList?.total_count ?? 0
      }
    }
    return {
      list: props.regularKekListState.regularKekList?.list ?? [],
      total_count: props.regularKekListState.regularKekList?.total_count ?? 0
    }
  }

  const getDisplayHttpStatus = (): number | null => {
    return props.isChecked ? props.hsmStatusState.httpStatus : props.regularKekListState.httpStatus
  }

  const getIsLoading = (): boolean => {
    return props.isChecked ? props.hsmStatusState.isLoading : props.regularKekListState.isLoading
  }

  const getError = (): string | null => {
    return props.isChecked ? props.hsmStatusState.error : props.regularKekListState.error
  }

  useEffect(() => {
    setRegularKekList(getDisplayKekList().list)
  }, [props.regularKekListState, props.hsmStatusState, props.isChecked, appliedFilters])

  const renderTable = (kekList: any[], totalCount: number): JSX.Element => (
        <>
            <Paper sx={{
              width: '100%',
              mb: 2,
              backgroundColor: '#272727',
              boxShadow: 'unset',
              color: '#F0F0F0',
              marginBottom: '0'
            }}>
                <TableContainer className="device-table">
                    <Table sx={{ minWidth: 750, color: '#f0f0f0' }} aria-labelledby="tableTitle" size={'small'}>
                        <EnhancedTableHead
                            numSelected={selected.filter((label) => kekList.map((kek: HsmRegularKEK) => kek.id).includes(label)).length}
                            order={sort.order}
                            orderBy={sort.orderBy}
                            onRequestSort={handleRequestSort}
                            onSelectAllClick={handleSelectAllClick}
                            rowCount={kekList.length}
                            dataList={kekList}
                        />
                        <TableBody>
                            {kekList.map((row: HsmRegularKEK, index: number) => {
                              const isItemSelected = isSelected(row.id)
                              const labelId = `enhanced-table-checkbox-${row.id}`
                              return (
                                    <StyledTableRow
                                        hover
                                        role="checkbox"
                                        aria-checked={isItemSelected}
                                        tabIndex={-1}
                                        key={row.id}
                                        aria-label="groups-list-row"
                                        selected={isItemSelected}
                                    >
                                        <StyledTableCell padding="checkbox">
                                            <Checkbox
                                                data-testid={`checkbox-${index}`}
                                                sx={{ color: '#909090' }}
                                                checked={isItemSelected}
                                                onChange={() => {
                                                  handleRowClick(row.id)
                                                }}
                                                inputProps={{
                                                  'aria-labelledby': labelId,
                                                  'aria-label': 'test-checkbox',
                                                  id: `select-item-${index}`
                                                }}
                                            />
                                        </StyledTableCell>
                                        <StyledTableCell
                                            component="th"
                                            id={labelId}
                                            scope="row"
                                            padding="none"
                                            aria-label="group-name"
                                            data-testid="group-name"
                                            sx={{
                                              whiteSpace: 'nowrap',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              maxWidth: '15vw',
                                              cursor: 'pointer'
                                            }}
                                        >
                                            {(row.label != null) && row.label.length > 12
                                              ? <abbr style={{ textDecoration: 'none' }}
                                                        title={row.label}>{row.label}</abbr>
                                              : row.label}
                                        </StyledTableCell>
                                        <StyledTableCell>{row.tenant}</StyledTableCell>
                                        <StyledTableCell>{getUseString(row.use)}</StyledTableCell>
                                        <StyledTableCell
                                            sx={{
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              maxWidth: '15vw',
                                              whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {(row.createdBy != null) && row.createdBy.length > 12
                                              ? <abbr style={{ textDecoration: 'none' }}
                                                        title={row.createdBy}>{row.createdBy}</abbr>
                                              : row.createdBy}
                                        </StyledTableCell>
                                        <StyledTableCell>{(row.createdAt != null) ? new Date(row.createdAt).toLocaleString() : ''}</StyledTableCell>
                                        <StyledTableCell>
                                            {row.use === 1 && (
                                                <span
                                                    data-testid={`delete-${index}`}
                                                    onClick={() => {
                                                      props.downloadRegularKekByLabel(row.label ?? '')
                                                    }}
                                                    style={{
                                                      color: '#64c3ff',
                                                      textDecoration: 'none',
                                                      cursor: 'pointer'
                                                    }}
                                                >
                        {HSMConfigurationRegularKekStrings.Download}
                      </span>
                                            )}
                                            {row.use === 3 && (
                                                <span
                                                    data-testid={`export-smime-${index}`}
                                                    onClick={() => {
                                                      props.exportSMimeCertificate(row.label ?? '')
                                                    }}
                                                    style={{
                                                      color: '#64c3ff',
                                                      textDecoration: 'none',
                                                      cursor: 'pointer'
                                                    }}
                                                >
                        {HSMConfigurationRegularKekStrings.Download}
                      </span>
                                            )}
                                        </StyledTableCell>
                                    </StyledTableRow>
                              )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                {totalCount > 10 && (
                    <Pagination
                        count={totalCount}
                        page={page}
                        setPage={setPage}
                        rowsPerPage={rowsPerPage}
                        siblingCount={1}
                        boundaryCount={1}
                    />
                )}
            </Paper>
            {selected.length === 0
              ? /* istanbul ignore next */ null
              : (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: '#272727',
                      borderTop: '2px solid #202020'
                    }}>
                        <GenericButton type='borderless' dataTestId='cancel-btn' onClick={() => {
                          setSelected([])
                        }}>
                            {HSMConfigurationRegularKekStrings.Cancel}
                        </GenericButton>
                    </div>
                )}
        </>
  )

  const handleTable = (): JSX.Element => {
    const displayKekList = getDisplayKekList()
    const displayHttpStatus = getDisplayHttpStatus()
    const isLoading = getIsLoading()
    const error = getError()

    const cryptoStatus = props.hsmStatusState.cryptoRequest?.requestStatus
    if (props.isChecked && (cryptoStatus === 'PROCESSING' || cryptoStatus === 'PENDING')) {
      return <div className='CircularprogressAmi'><CircularProgress/></div>
    }

    if (props.isChecked && (cryptoStatus === 'FAILED' || getError() === 'Request failed')) {
      return <div className='authError'><AuthError errorMessage="cannotFetch"
        retry={() => { isHsmWithFilters ? handleFetchRegularKekHsmListWithStatusAndFilters() : handleFetchRegularKekHsmListWithStatus() }}/></div>
    }

    if (isLoading) {
      return <div className='CircularprogressAmi'><CircularProgress/></div>
    }

    if (error != null) {
      return <div className='authError'><AuthError errorMessage={error}/></div>
    }

    if (displayKekList.total_count > 0 && displayHttpStatus === 200) {
      return renderTable(displayKekList.list, displayKekList.total_count)
    }

    if (displayHttpStatus === 401) {
      return <div className='authError'><AuthError errorMessage="Unauthorized"/></div>
    }
    if (displayHttpStatus === 403) {
      return <div className='authError'><AuthError errorMessage="accessForbidden"/></div>
    }
    if (displayHttpStatus === 404) {
      return <div className='authError'><AuthError errorMessage="NoDataPresent"/></div>
    }
    if (displayHttpStatus === 400) {
      return <div className="authError"><AuthError errorMessage="invalidSearchInput"/></div>
    }

    if (displayHttpStatus === 200 && displayKekList.total_count === 0) {
      return <div className='authError'><AuthError errorMessage="NoDataPresent"/></div>
    }

    if (!props.isChecked && displayHttpStatus === 500) {
      return <div className='authError'><AuthError errorMessage="cannotFetch"
                                                         /></div>
    }

    if ((appliedFilters != null) && displayHttpStatus === 500) {
      return <div className='authError'><AuthError errorMessage="cannotFetch"
                                                         retry={() => { showFilterDetails(page - 1) }}/></div>
    }
    return <div className='authError'><AuthError errorMessage="cannotFetch"
                                                     retry={() => { handleFetchRegularKEKList() }}/></div>
  }

  return (
        <>
            <Box sx={style} className="devices">
                <div className='searchSection' style={{ width: '100%', backgroundColor: '#272727' }}>
                    <Paper className='searchBoxComponent'
                           sx={{ p: '2px 4px', display: 'flex', alignItems: 'center' }}
                    >
                        <Button data-testid='filter-data' className="filter-btn"
                                sx={{ background: '#707070', color: '#D0D0D0' }} onClick={() => {
                                  setOpenFilter(true)
                                }}><img src={FilterIcon} height={16} width={16}
                                style={{ marginLeft: '7px' }}/>{HSMConfigurationRegularKekStrings.Filter}</Button>
                        <SearchBox immediate={true} debounce={true}
                                   placeholder={HSMConfigurationRegularKekStrings.SearchUsingTenantNameCategoryCreatedBy}
                                   sx={{ width: '450px' }} emptyEnterFallback={() => {
                                     props.isChecked && page >= 1
                                       ? props.fetchRegularKekHsmListWithStatus(page - 1, rowsPerPage, sort.order, sort.orderBy, '')
                                       : props.fetchRegularKEKList(page - 1, rowsPerPage, sort.order, sort.orderBy, '')
                                   }} searchVal={searchValue} setSearchVal={setSearchValue}
                                   performAction={() => {
                                     handleSearch()
                                   }}/>
                        <FormControlLabel
                            aria-label="status-change"
                            sx={{ color: '#909090' }}
                            control={<Checkbox
                                sx={{ color: '#909090' }}
                                data-testid="device-table-header-checkbox"
                                checked={props.isChecked}
                                onChange={handleCheckboxChange}
                                inputProps={{ id: 'select-item' }}
                            />}
                            label={HSMConfigurationRegularKekStrings.ReadFromHSM}
                        />
                    </Paper>
                </div>
                <Drawer
                    anchor="right"
                    open={openFilter}
                    className='filter-drawer'
                    variant='temporary'
                    onClose={() => {
                      setOpenFilter(false)
                      resetFilterState()
                    }}
                >
                    <div className='filter-headar'>
                        <div className='title'
                             style={{ marginTop: '15px' }}>{HSMConfigurationRegularKekStrings.Filter}</div>
                        <div><Button aria-label='clear-all-filter' className='cancel'
                                     style={{ marginTop: '-2px', marginLeft: '80px' }} onClick={() => {
                                       setFilterState({ use: null })
                                     }}><ReplayIcon sx={{
                                       width: '16px',
                                       height: '16px',
                                       marginRight: '8px'
                                     }}/>{HSMConfigurationRegularKekStrings.ClearAll}</Button></div>
                        <div><CloseIcon aria-label='close-drawer' sx={{ color: '#f0f0f0', cursor: 'pointer' }}
                                        onClick={() => {
                                          setOpenFilter(false)
                                          resetFilterState()
                                        }}/>
                        </div>
                    </div>
                    <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                        <Grid item xs={12}>
                            <div className='type-name'
                                 style={{ marginTop: '40px' }}>{HSMConfigurationRegularKekStrings.UseType}</div>
                            <GenXDropdown placeholder={HSMConfigurationRegularKekStrings.SelectUseType}
                                          value={filterState.use} options={useOptions} onSelect={handleUseSelect}/>
                        </Grid>
                    </Grid>
                    <div className='divider'/>
                    <div className='buttons-container'>
                        <Button className='cancel' aria-label='cancel-drawer' onClick={() => {
                          resetFilterState()
                          setOpenFilter(false)
                        }}>{HSMConfigurationRegularKekStrings.Cancel}</Button>
                        <GenericButton aria-label='confirm-filter' data-testid='show-results'
                                       primaryColor={!filtersUpdated() ? '#B0B0B0' : undefined}
                                       disabled={!filtersUpdated()}
                                       onClick={() => {
                                         setAppliedFilters(filterState)
                                         setShowFilter(true)
                                         setOpenFilter(false)
                                         setPage(1)
                                         if (!props.isChecked) {
                                           showFilterDetails(0)
                                         } else {
                                           props.fetchRegularKekHsmListWithStatusAndFilters(0, rowsPerPage, sort.order, sort.orderBy, searchValue ?? '', { use: filterState.use === null ? [] : [filterState.use.value] })
                                         }
                                         setSearchValue(null)
                                       }}>{HSMConfigurationRegularKekStrings.ShowDetails}</GenericButton>
                    </div>
                </Drawer>
                {showfilter &&
                  <div
                    style={{
                      padding: '12px 24px',
                      borderTop: '2px solid #202020',
                      color: '#d0d0d0'
                    }}
                  >
                      {appliedFilters.use === null
                        ? (
                            HSMConfigurationRegularKekStrings.NoFiltersApplied
                          )
                        : (
                              <div className="classNAme">
                                  {numAssignedAssets} {HSMConfigurationRegularKekStrings.ResultsOrFilters}
                                  <>
                                      {<div className="box">
                                          {' '}
                                          {HSMConfigurationRegularKekStrings.Use} : {' '}
                                          {appliedFilters.use.label}
                                      </div>
                                      }
                                  </>
                                  <em
                                      onClick={() => {
                                        resetAppliedFilters()
                                        setFilterState({ use: null })
                                      }}
                                      style={{ cursor: 'pointer' }}
                                  >
                                      {HSMConfigurationRegularKekStrings.ClearFilters}{' '}
                                  </em>
                              </div>
                          )}
                  </div>
                }
                {handleTable()}
            </Box>
        </>
  )
}

interface DispatchToProps {
  fetchRegularKEKList: (page: number, size: number, sortdir: string, sortfield: string, input: string) => void
  getAllRegularKekFilterParameterInfo: (page: number, size: number, sortdir: string, sortfield: string, input: string, filterParams: any) => void
  getAllRegularKekParameterInfo: () => void
  downloadRegularKekByLabel: (label: string) => void
  fetchRegularKekHsmListWithStatus: (page: number, size: number, sortdir: string, sortfield: string, input: string) => void
  fetchRegularKekHsmListWithStatusAndFilters: (page: number, size: number, sortdir: string, sortfield: string, input: string, filterParams: any) => void
  exportSMimeCertificate: (label: string) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  getAllRegularKekParameterInfo: () => dispatch(getAllRegularKekParameterInfo()),
  getAllRegularKekFilterParameterInfo: (page: number, size: number, sortdir: string, sortfield: string, input: string, filterParams: any) => dispatch(getAllRegularKekFilterParameterInfo(page, size, sortdir, sortfield, input, filterParams)),
  fetchRegularKEKList: (page: number, size: number, sortdir: string, sortfield: string, input: string) => dispatch(fetchRegularKEKList(page, size, sortdir, sortfield, input)),
  downloadRegularKekByLabel: (label: string) => dispatch(downloadregularKekByLabel(label)),
  fetchRegularKekHsmListWithStatus: (page: number, size: number, sortdir: string, sortfield: string, input: string) => dispatch(fetchRegularKekHsmListWithStatus(page, size, sortdir, sortfield, input)),
  fetchRegularKekHsmListWithStatusAndFilters: (page: number, size: number, sortdir: string, sortfield: string, input: string, filterParams: any) => dispatch(fetchRegularKekHsmListWithStatusAndFilters(page, size, sortdir, sortfield, input, filterParams)),
  exportSMimeCertificate: (label: string) => dispatch(exportSMimeCertificate(label))
})

interface StateToProps {
  regularKekListState: RegularKekListState
  regularKekFilterState: getAllRegularKekParameterState
  hsmStatusState: RegularKekHsmStatusState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  regularKekListState: state.regularKekList,
  regularKekFilterState: state.getAllRegularKekParameterSlice,
  hsmStatusState: state.regularKekHsmStatus

})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>
export default connector(RegularKekTableComponent)
